import { ErrorMessage } from "@atlaskit/form";
import { currencyFormatter } from "@fla/common";
import {
  AssetType,
  Button,
  ConsentOrdersPathName,
  FormNavigation,
  OverseasAssetBaseProps,
  OverseasAssetBaseSchema,
  OverseasAsset_InitialState,
  RadioSelect,
  useNames,
  useOptions,
  useOverseas
} from "@fla/components";
import OverseasIcon from "@fla/components/src/assets/svgs/overseas.svg";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAppDispatch } from "app/store";
import { withConsentOrder } from "components/HOCs/withConsentOrder";
import { useConsentOrders } from "hooks/ConsentOrders";
import { SliceStatus } from "interfaces";
import { selectAsset, selectBaseAssetsDebts, selectConsentOrdersBase } from "pages/ConsentOrders";
import { consentOrdersActions } from "pages/ConsentOrders/consentOrdersSlice";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

const defaultValues = {
  if_own_asset_overseas: "no"
};

function OverseasAsset() {
  const { initialDetails } = useSelector(selectConsentOrdersBase);
  const overseasData = useSelector(selectAsset(AssetType.OVERSEAS)) as OverseasAsset_InitialState[];
  const _useNames = useNames({ initialDetails });
  const { spouseFirstName } = _useNames;
  const _useOptions = useOptions({ initialDetails });
  const assetsDebts = useSelector(selectBaseAssetsDebts);
  const [status, setStatus] = useState(SliceStatus.Idle);
  const [error, setError] = useState<string>();

  const location = useLocation();
  const navigate = useNavigate();
  const { handleSubmit, watch, control, reset, getValues } = useForm({
    mode: "all",
    defaultValues,
    resolver: yupResolver(OverseasAssetBaseSchema)
  });

  const handleBackClick = () => {
    navigate(ConsentOrdersPathName.Business);
  };

  const editData = (values?: OverseasAssetBaseProps) => {
    navigate(`${ConsentOrdersPathName.Overseas}/add`, {
      state: {
        parent: getValues(),
        child: values
      }
    });
  };

  const dispatch = useAppDispatch();
  const { updateAssetsDebts } = useConsentOrders();

  const onSubmit = async (values: OverseasAssetBaseProps) => {
    try {
      setStatus(SliceStatus.Pending);

      if (values.if_own_asset_overseas === "no" && overseasData.length) {
        setError("If neither of you have any overseas asset, please remove the added overseas asset.");
      } else if (values.if_own_asset_overseas === "yes" && !overseasData.length) {
        setError("please add overseas asset details");
      } else {
        setError("");

        const payload = {
          overseas: { data: values, isCompleted: true },
          currentStep: 8
        };
        await updateAssetsDebts(payload);
        dispatch(consentOrdersActions.setAssetsDebts(payload));
        navigate(ConsentOrdersPathName.Furniture);
      }
      setStatus(SliceStatus.Resolved);
    } catch (error) {
      setStatus(SliceStatus.Rejected);
    }
  };

  useEffect(() => {
    if (location.state) {
      reset(location.state);
    } else {
      reset(defaultValues);
    }
  }, [location]);

  const { ownCard, splitCard, userTotalAmount, spouseTotalAmount } = useOverseas({
    overseasData,
    ..._useNames,
    ..._useOptions
  });

  return (
    <section className="w-full py-10">
      <form
        id="overseas-asset-form"
        className="md:w-10/12 lg:max-w-2xl mx-auto h-fit px-5 md:px-10 md:py-5 space-y-5"
        onSubmit={handleSubmit(onSubmit)}
      >
        <img src={OverseasIcon} className="w-10 h-auto mx-auto my-3" alt="overseas asset" />

        <h3 className="text-center my-5">Overseas Asset</h3>

        <RadioSelect
          title={`Do you or ${spouseFirstName} own an have any Assets overseas?`}
          name="if_own_asset_overseas"
          id="if_own_asset_overseas"
          control={control}
          options={[
            { label: "Yes", value: "yes" },
            { label: "No", value: "no" }
          ]}
          type="card"
          onChange={() => setError("")}
          theme="dark"
        />

        <div className="w-full py-10 border border-gray-400 border-dashed rounded-lg flex flex-col items-center justify-center space-y-10 mb-5">
          <p className="px-16 text-center text-base font-bold">
            Please add all the overseas assets you and {spouseFirstName} have in Australia
          </p>
          <Button
            className="px-5 py-2 uppercase"
            fontSize="sm"
            onClick={() => editData(undefined)}
            disabled={watch("if_own_asset_overseas") !== "yes"}
          >
            + Add Overseas Asset
          </Button>
        </div>

        {error ? (
          <div className="max-w-sm mx-auto text-center">
            <ErrorMessage>{error}</ErrorMessage>
          </div>
        ) : null}

        {Array.isArray(overseasData) && overseasData.length ? (
          <>
            <p className="uppercase text-gray-500 text-xl font-medium my-5">You’re Keeping</p>

            {ownCard("user", editData)}
            {splitCard("user", editData)}
            <section className="flex justify-end items-center mb-12">
              <p className="text-gray-600">
                Total:
                <span className="text-2xl ml-2">{currencyFormatter.format(userTotalAmount)}</span>
              </p>
            </section>

            <p className="uppercase text-gray-500 text-xl font-medium my-5">{spouseFirstName} Keeping</p>

            {ownCard("spouse", editData)}
            {splitCard("spouse", editData)}
            <section className="flex justify-end items-center mb-12">
              <p className="text-gray-600">
                Total:
                <span className="text-2xl ml-2">{currencyFormatter.format(spouseTotalAmount)}</span>
              </p>
            </section>
          </>
        ) : null}

        <FormNavigation
          isFirst={false}
          isLast={false}
          form="overseas-asset-form"
          handleBackClick={handleBackClick}
          nextButtonText="Continue"
          isSubmitting={status === SliceStatus.Pending}
          classes="mt-12"
        />
      </form>
    </section>
  );
}
export default withConsentOrder(OverseasAsset);
